<template>
  <div v-if="isAssistConversation" class="assist-input">
    <div class="input-tabs">
      <div
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-item"
        :class="{ selected: selectedTab === tab.id }"
        @click="handleTab(tab.id)"
      >
        <slot v-if="shouldDisplayPromptsTab">
          <BbIcon size="small" :name="tab.icon" />
          <TextBody :i18n-key="tab.label" tag="span" :body-type="1" weight="medium" />
        </slot>
      </div>
    </div>

    <div v-if="displayPromptCardsList">
      <PromptCardSelectCategory v-if="categories?.length" @select-categories="selectCategories" />
      <PromptCardsList
        :prompts="prompts"
        :showMoreButton="true"
        @open-more="isMoreCardsModalOpened = true"
        @select-prompt="onSelectPrompt"
      />
    </div>
    <hr v-if="!displayPromptsInNewTab && messages.length === 0" class="divider" />
    <InputField v-if="selectedTab === 0" :rows="5" v-model="sharedValue" @onSubmit="onSubmit" />
    <PromptCardInput v-if="displayPromptCardsInput" v-model="sharedValue" @onSubmit="onSubmit" />
    <FilterForm v-if="selectedTab === 2" />
  </div>
  <InputField v-else v-model="sharedValue" @onSubmit="onSubmit" />

  <PromptCardsModal
    v-if="isMoreCardsModalOpened"
    :isOpen="isMoreCardsModalOpened"
    @select-prompt="onSelectPrompt"
    @select-categories="selectCategories"
    @close="isMoreCardsModalOpened = false"
  />
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { marked } from 'marked';

import { Routes } from '~/constants/routes';
import { tabs } from '~/constants/inputTabs';
import { isSuggestedCategory } from '~/constants/conversationModels';

import PromptCardInput from '~/components/conversation/quickPrompting/PromptCardInput.vue';
import InputField from '~/components/conversation/InputField.vue';
import TextBody from '~/components/common/TextBody.vue';
import { useSanitize } from '~/composables/useSanitize';
import FilterForm from '~/services/filter-form-service/components/FilterForm.vue';
import useModals from '~/composables/useModals';
import useConversations from '~/composables/useConversations';
import PromptCardsModal from '~/components/conversation/quickPrompting/PromptCardsModal.vue';
import useQuickPrompts from '~/composables/useQuickPrompts';
import PromptCardsList from '~/components/conversation/quickPrompting/PromptCardsList.vue';
import PromptCardSelectCategory from '~/components/conversation/quickPrompting/PromptCardSelectCategory.vue';

export default {
  components: {
    PromptCardSelectCategory,
    PromptCardsList,
    FilterForm,
    PromptCardInput,
    InputField,
    TextBody,
    PromptCardsModal,
  },
  emits: ['onSubmit', 'select-prompt'],
  setup(props, { emit }) {
    const route = useRoute();
    const { sanitize } = useSanitize(true);
    const { setShowFilters } = useModals();
    const { currentConversation } = useConversations();
    const {
      setDisplayPromptsInNewTab,
      displayPromptsInNewTab,
      onSelectPrompt,
      prompts,
      currentPromptCard,
      setCurrentPromptCard,
      setCurrentFields,
      getPromptCardsByCategory,
      getCategories,
      setSelectedCategories,
      categories,
    } = useQuickPrompts();

    const applyLimit = (array) => array?.slice(0, window.innerWidth > 1120 ? 8 : window.innerWidth > 754 ? 5 : 2);

    const currentRouteName = route.name;
    const messages = computed(() => currentConversation.value.messages);
    const displayPromptCardsList = computed(
      () =>
        (selectedTab.value === 0 && messages.value.length === 0) ||
        (!currentPromptCard.value?.id && selectedTab.value === 1),
    );
    const displayPromptCardsInput = computed(
      () => displayPromptsInNewTab.value && currentPromptCard.value?.id && selectedTab.value === 1,
    );
    const currentPrompts = ref([]);

    const sharedValue = ref('');
    const selectedTab = ref(0);
    const isMoreCardsModalOpened = ref(false);
    const windowWidth = ref(window.innerWidth);

    const shouldDisplayPromptsTab = () =>
      (messages.value.length === 0 && currentPromptCard.value?.id) || displayPromptsInNewTab.value || true;

    const handleTab = (index) => {
      if (index === tabs.find((item) => item.type === 'filters')?.id) {
        setShowFilters(true);
      }

      selectedTab.value = index;
      sharedValue.value = '';

      if (index === 0) {
        setDisplayPromptsInNewTab(false);
      }
    };

    const onSubmit = () => {
      const sanitizedInput = sanitize(marked(sharedValue.value));
      setCurrentPromptCard(currentPromptCard.value); //make sure value is not lost while tab is changed
      emit('onSubmit', sanitizedInput);
      // wait for message to be sent in order to make sure state is not cleared before
      setTimeout(() => {
        selectedTab.value = 0;
      }, 500);
    };

    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
      currentPrompts.value = applyLimit(prompts.value);
    };

    const selectCategories = (categoryIds) => {
      fetchPromptsByCategory(categoryIds);
      setSelectedCategories(categoryIds);
    };

    const fetchPromptsByCategory = async (categories = []) => {
      const categoriesIds = categories.filter((id) => id !== isSuggestedCategory.id);
      const prompts = await getPromptCardsByCategory({
        categories: categoriesIds || [],
        ...(!categories?.length || categories?.includes(isSuggestedCategory.id) ? { isSuggested: true } : {}),
      });
      currentPrompts.value = applyLimit(prompts);
    };

    onMounted(async () => {
      window.addEventListener('resize', updateWidth);
      await getCategories();
      await fetchPromptsByCategory();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateWidth);
    });

    watch(displayPromptsInNewTab, (newValue) => {
      if (newValue === true) {
        selectedTab.value = 1;
      }
    });

    watch(
      selectedTab,
      (newValue) => {
        if (newValue === 2) {
          setDisplayPromptsInNewTab(false);
        }
      },
      { immediate: true },
    );

    watch(
      selectedTab,
      (newValue) => {
        if (newValue === 1) {
          setDisplayPromptsInNewTab(true);
        }
      },
      { immediate: true },
    );

    return {
      sharedValue,
      isAssistConversation: currentRouteName === Routes.Conversation.name,
      tabs,
      selectedTab,
      handleTab,
      onSubmit,
      displayPromptsInNewTab,
      onSelectPrompt,
      isMoreCardsModalOpened,
      prompts: currentPrompts,
      shouldDisplayPromptsTab,
      messages,
      displayPromptCardsList,
      displayPromptCardsInput,
      selectCategories,
      categories,
    };
  },
};
</script>

<style scoped lang="scss">
.assist-input {
  border-radius: 24px;
  border: 1px solid $bb-neutral-4;

  .bb-textarea {
    padding: $bb-spacing-6;

    :deep(.v-field__outline) {
      display: none !important;
    }

    :deep(.v-field__append-inner) {
      background: red;

      button {
        position: absolute;
        bottom: -16px;
        right: -16px;
      }
    }
  }

  .input-tabs {
    display: flex;
    align-items: center;
    cursor: pointer;
    background: $bb-neutral-6;
    border-radius: 24px 24px 0 0;

    .tab-item {
      padding: $bb-spacing-4;
      display: flex;
      align-items: center;
      gap: $bb-spacing-2;

      &:last-of-type {
        margin-left: auto;
      }
    }

    .selected {
      background: $bb-neutral-0;
      border-radius: 24px 24px 0 0;
    }
  }
}

.divider {
  border: 0;
  height: 1px;
  background-color: #ccc;
}
</style>
